import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { EmbarqueResposta, SituacaoEmbarque } from '@pcp/contracts';
import { baixarExportacao, useEmbarques } from '../../api/embarques';
import { useMe } from '../../api/queries';
import { EmptyState, ErrorState, LoadingBlock } from '../../components/states';
import { formatDate, formatKg } from '../../format';
import { t } from '../../i18n/pt-BR';
import { PERFIS_ESCRITA } from './shared';

const s = t.shipments;

const TONE: Record<number, string> = { 1: 'info', 2: 'info', 3: 'warn', 4: 'ok', 5: 'warn', 6: 'bad', 7: 'info' };

export const SituacaoChip = ({ situacao }: { situacao: SituacaoEmbarque }) => (
  <span className={`chip ${TONE[situacao]}`}>{s.status[situacao]}</span>
);

/** Importação em Andamento: embarques em curso e histórico, com contador de atrasados e exportação. */
export function ImportacaoPage() {
  const me = useMe();
  const navigate = useNavigate();
  const [aba, setAba] = useState<'andamento' | 'historico'>('andamento');
  const [situacao, setSituacao] = useState('');
  const [item, setItem] = useState('');
  const [nr, setNr] = useState('');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [soAtrasados, setSoAtrasados] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const podeEscrever = !!me.data && PERFIS_ESCRITA.includes(me.data.perfil);
  const filtros = {
    grupo: situacao ? undefined : aba,
    situacao: situacao || undefined,
    item: item.trim() || undefined,
    'nr-import': nr.trim() || undefined,
    de: de || undefined,
    ate: ate || undefined,
    atrasado: soAtrasados ? true : undefined,
  };
  const lista = useEmbarques(filtros);
  // o contador do topo conta atrasados da aba "em andamento" sem depender dos filtros de texto
  const todosAndamento = useEmbarques({ grupo: 'andamento' });
  const atrasados = (todosAndamento.data ?? []).filter((e) => e.atrasado).length;

  const exportar = async (formato: 'xlsx' | 'csv') => {
    setExportError(null);
    try {
      await baixarExportacao(formato, filtros);
    } catch (e) {
      setExportError(e instanceof Error ? e.message : t.states.datasulDown);
    }
  };

  const opcoes = aba === 'andamento' ? [1, 2, 3, 5, 7] : [4, 6];

  return (
    <section className="panel" aria-labelledby="emb-title">
      <h2 id="emb-title">{s.title}</h2>
      <p className="hint">{s.hint}</p>

      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div className="row">
          {(['andamento', 'historico'] as const).map((k) => (
            <button
              key={k}
              type="button"
              className={`btn ${aba === k ? '' : 'ghost'}`}
              aria-pressed={aba === k}
              onClick={() => {
                setAba(k);
                setSituacao('');
              }}
            >
              {k === 'andamento' ? s.tabs.progress : s.tabs.history}
            </button>
          ))}
        </div>
        <div className="row">
          <span className={`chip ${atrasados ? 'bad' : 'ok'}`} role="status">
            {atrasados ? s.lateCounter(atrasados) : s.noLate}
          </span>
        </div>
      </div>

      <div className="row" style={{ flexWrap: 'wrap', gap: 12, marginTop: 14 }}>
        <label className="inline-label" htmlFor="f-sit">
          {s.filters.status}
        </label>
        <select id="f-sit" className="select" value={situacao} onChange={(e) => setSituacao(e.target.value)}>
          <option value="">{s.allStatuses}</option>
          {opcoes.map((n) => (
            <option key={n} value={n}>
              {s.status[n]}
            </option>
          ))}
        </select>
        <label className="inline-label" htmlFor="f-item">
          {s.filters.item}
        </label>
        <input id="f-item" className="select" value={item} onChange={(e) => setItem(e.target.value)} />
        <label className="inline-label" htmlFor="f-nr">
          {s.filters.nr}
        </label>
        <input id="f-nr" className="select" value={nr} onChange={(e) => setNr(e.target.value)} />
        <label className="inline-label" htmlFor="f-de">
          {s.filters.from}
        </label>
        <input id="f-de" type="date" className="select" value={de} onChange={(e) => setDe(e.target.value)} />
        <label className="inline-label" htmlFor="f-ate">
          {s.filters.to}
        </label>
        <input id="f-ate" type="date" className="select" value={ate} onChange={(e) => setAte(e.target.value)} />
        <label className="check">
          <input type="checkbox" checked={soAtrasados} onChange={(e) => setSoAtrasados(e.target.checked)} />
          <span>{s.onlyLate}</span>
        </label>
      </div>

      <div className="row" style={{ marginTop: 14, flexWrap: 'wrap' }}>
        {podeEscrever ? (
          <>
            <Link className="btn" to="/importacao/novo">
              {s.newShipment}
            </Link>
            <Link className="btn ghost" to="/importacao/carga-inicial">
              {s.initialLoad}
            </Link>
          </>
        ) : null}
        <button type="button" className="btn ghost" onClick={() => void exportar('xlsx')}>
          {s.export} · {s.exportXlsx}
        </button>
        <button type="button" className="btn ghost" onClick={() => void exportar('csv')}>
          {s.export} · {s.exportCsv}
        </button>
      </div>
      {exportError ? (
        <div className="form-error" role="alert">
          {exportError}
        </div>
      ) : null}

      <div style={{ marginTop: 16 }}>
        {lista.isLoading ? (
          <LoadingBlock rows={4} />
        ) : lista.isError ? (
          <ErrorState error={lista.error} onRetry={() => void lista.refetch()} />
        ) : !lista.data?.length ? (
          <EmptyState title={s.title}>{s.empty}</EmptyState>
        ) : (
          <Tabela dados={lista.data} onOpen={(nrImport) => navigate(`/importacao/${encodeURIComponent(nrImport)}`)} />
        )}
      </div>
    </section>
  );
}

function Tabela({ dados, onOpen }: { dados: EmbarqueResposta[]; onOpen: (nr: string) => void }) {
  return (
    <div className="scroll">
      <table className="cad-table">
        <thead>
          <tr>
            <th>{s.columns.nr}</th>
            <th>{s.columns.status}</th>
            <th>{s.columns.exporter}</th>
            <th>{s.columns.items}</th>
            <th>{s.columns.eta}</th>
            <th>{s.columns.delivery}</th>
            <th>{s.columns.late}</th>
          </tr>
        </thead>
        <tbody>
          {dados.map((e) => (
            <tr key={e['nr-import']} className={e.atrasado ? 'conflict' : undefined}>
              <td>
                <button type="button" className="link" onClick={() => onOpen(e['nr-import'])}>
                  <strong>{e['nr-import']}</strong>
                </button>
              </td>
              <td>
                <SituacaoChip situacao={e.situacao} />
              </td>
              <td>{e.exportador ?? '—'}</td>
              <td>
                {e.itens.map((i) => (
                  <div key={i.seq}>
                    {i['it-codigo']} · {formatKg(i.quantidade)} {i.un}
                  </div>
                ))}
              </td>
              <td>{e['dt-eta-prev'] ? formatDate(e['dt-eta-prev']) : '—'}</td>
              <td>{e['dt-entrega-prev'] ? formatDate(e['dt-entrega-prev']) : <em>{s.noDate}</em>}</td>
              <td>{e.atrasado ? <span className="chip bad">{s.late}</span> : null}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
